import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, MicOff, Send, Loader2, Users } from "lucide-react";
import { useApp } from "../context/AppContext";
import { askCouncil } from "../lib/api";
import { Markdown } from "../lib/markdown";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { Paywall } from "../components/Paywall";
import { toast } from "sonner";

const COUNCIL_NAMES = ["GPT-5.4 Mini", "Claude Haiku 4.5", "Gemini 3 Flash"];

export default function Ask() {
  const { canAsk, remaining, unlocked, addSession } = useApp();
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [paywall, setPaywall] = useState(false);
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);

  const speechSupported = typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => () => { if (recRef.current) recRef.current.stop(); }, []);

  const toggleMic = () => {
    if (!speechSupported) { toast.error("Voice input isn't supported in this browser."); return; }
    if (listening) { recRef.current && recRef.current.stop(); setListening(false); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = "en-US";
    let base = question ? question + " " : "";
    rec.onresult = (e) => {
      let txt = "";
      for (let i = e.resultIndex; i < e.results.length; i++) txt += e.results[i][0].transcript;
      setQuestion(base + txt);
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec; rec.start(); setListening(true);
  };

  const ask = async () => {
    if (!question.trim()) return;
    if (!canAsk) { setPaywall(true); return; }
    if (listening && recRef.current) { recRef.current.stop(); setListening(false); }
    setLoading(true); setResult(null);
    try {
      const data = await askCouncil(question.trim());
      const session = {
        id: crypto.randomUUID(),
        createdAt: Date.now(),
        question: data.question,
        raw_answers: data.raw_answers,
        synthesis: data.synthesis,
        tags: data.tags || [],
      };
      await addSession(session);
      setResult(session);
      toast.success("Saved to your Library");
    } catch (e) {
      toast.error("The council couldn't be reached. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-10">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black tracking-tighter" style={{ color: "#1E293B" }}>Ask the Council</h1>
          <p className="mt-1" style={{ color: "#475569" }}>One question. Three minds. One synthesized answer.</p>
        </div>
        {!unlocked && (
          <span className="text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: "#EFF6FF", color: "#2563EB" }}>
            {remaining} free {remaining === 1 ? "session" : "sessions"} left
          </span>
        )}
      </div>

      <div className="mt-6 rounded-2xl border bg-white shadow-sm overflow-hidden" style={{ borderColor: "#CBD5E1" }}>
        <textarea
          data-testid="question-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ask anything — e.g. How should I approach a career change at 35?"
          className="w-full min-h-[150px] p-5 text-base resize-y outline-none"
          style={{ color: "#1E293B" }}
        />
        <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: "#E2E8F0", background: "#F8FAFC" }}>
          <button onClick={toggleMic} data-testid="mic-input"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold border transition-colors duration-200"
            style={{ borderColor: listening ? "#2563EB" : "#CBD5E1", color: listening ? "#2563EB" : "#475569", background: "#fff" }}>
            {listening ? <MicOff size={16} className="pulse-soft" /> : <Mic size={16} />}
            {listening ? "Listening…" : "Speak"}
          </button>
          <button onClick={ask} disabled={loading || !question.trim()} data-testid="ask-council-btn"
            className="inline-flex items-center gap-2 h-10 px-6 rounded-xl text-sm font-bold text-white transition-colors duration-200 disabled:opacity-50"
            style={{ background: "#2563EB" }}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.background = "#1D4ED8")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#2563EB")}>
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Ask the Council
          </button>
        </div>
      </div>

      {loading && (
        <div className="mt-8" data-testid="loading-state">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: "#94A3B8" }}>The council is thinking…</p>
          <div className="grid sm:grid-cols-3 gap-4">
            {COUNCIL_NAMES.map((n) => (
              <div key={n} className="rounded-2xl border bg-white p-5 shadow-sm" style={{ borderColor: "#CBD5E1" }}>
                <div className="flex items-center gap-2 mb-3">
                  <Users size={15} color="#2563EB" />
                  <span className="text-sm font-semibold" style={{ color: "#1E293B" }}>{n}</span>
                </div>
                <div className="space-y-2">
                  <div className="h-3 rounded pulse-soft" style={{ background: "#E2E8F0" }} />
                  <div className="h-3 rounded pulse-soft w-5/6" style={{ background: "#E2E8F0" }} />
                  <div className="h-3 rounded pulse-soft w-2/3" style={{ background: "#E2E8F0" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {result && (
        <div className="mt-8 fade-up" aria-live="polite">
          <div className="rounded-2xl border-2 bg-white p-7 shadow-sm" style={{ borderColor: "#2563EB33" }} data-testid="synthesis-result">
            <div className="flex items-center gap-2 mb-3">
              <span className="grid place-items-center w-7 h-7 rounded-lg" style={{ background: "#2563EB" }}>
                <Users size={15} color="#fff" />
              </span>
              <span className="text-sm font-bold" style={{ color: "#1E293B" }}>Synthesized Answer</span>
            </div>
            <Markdown text={result.synthesis} />
            {result.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-5">
                {result.tags.map((t) => (
                  <span key={t} className="text-xs font-semibold px-3 py-1 rounded-full" style={{ background: "#ECFDF5", color: "#047857" }}>
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>

          <Accordion type="single" collapsible className="mt-5">
            <AccordionItem value="raw" className="border rounded-2xl bg-white px-5" style={{ borderColor: "#CBD5E1" }}>
              <AccordionTrigger data-testid="raw-answers-toggle" className="text-sm font-semibold" style={{ color: "#1E293B" }}>
                See the {result.raw_answers.length} raw council answers
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-5 pb-2">
                  {result.raw_answers.map((a) => (
                    <div key={a.model}>
                      <p className="text-xs font-bold uppercase tracking-[0.15em] mb-2" style={{ color: "#2563EB" }}>{a.model}</p>
                      <Markdown text={a.answer} />
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="mt-6 flex gap-3">
            <button onClick={() => { setResult(null); setQuestion(""); }} data-testid="ask-another-btn"
              className="h-11 px-5 rounded-xl text-sm font-bold border transition-colors duration-200"
              style={{ borderColor: "#CBD5E1", color: "#1E293B", background: "#fff" }}>
              Ask another
            </button>
            <button onClick={() => navigate("/library")} data-testid="go-library-btn"
              className="h-11 px-5 rounded-xl text-sm font-bold text-white transition-colors duration-200"
              style={{ background: "#10B981" }}>
              View in Library
            </button>
          </div>
        </div>
      )}

      <Paywall open={paywall} onOpenChange={setPaywall} />
    </div>
  );
}
